import { useState, useEffect, useCallback, useRef } from "react";

export interface AnalyticsSettings {
  // Chart settings
  chartHeight: number;
  chartViewport: {
    startTime: number;
    endTime: number;
  } | null;

  // UI state
  autoCenter: boolean;
  showGrid: boolean;

  // Video settings
  selectedVideoId: string | null;
  videoPlaybackRate: number;
  videoVolume: number;
  videoCurrentTime: number;

  // Sync settings
  syncOffset: number;

  // Data channels
  selectedChannels: string[];
  selectedChannelColorIndices: Record<string, number>;

  // Layout preferences
  sidebarCollapsed?: boolean;
  leftPanelSize?: number;
  middlePanelSize?: number;
  rightPanelSize?: number;
}

const DEFAULT_SETTINGS: AnalyticsSettings = {
  chartHeight: 200, // Medium size by default
  chartViewport: null,
  autoCenter: true,
  showGrid: true,
  selectedVideoId: null,
  videoPlaybackRate: 1.0,
  videoVolume: 0.8,
  videoCurrentTime: 0,
  syncOffset: 0,
  selectedChannels: [],
  selectedChannelColorIndices: {},
  sidebarCollapsed: false,
  leftPanelSize: 15,
  middlePanelSize: 65,
  rightPanelSize: 20,
};

function readStoredSettings(storageKey: string): AnalyticsSettings {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle missing fields in stored data
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (error) {
    console.warn("Failed to load analytics settings from localStorage:", error);
  }
  return DEFAULT_SETTINGS;
}

/**
 * Hook for managing analytics page settings with localStorage persistence
 * Settings are stored per-session to allow different configurations for different sessions
 */
export function useAnalyticsSettings(sessionId: string) {
  const storageKey = `analytics-settings-${sessionId}`;

  // Initialize state from localStorage or defaults
  const [settings, setSettings] = useState<AnalyticsSettings>(() =>
    readStoredSettings(storageKey),
  );

  // React reuses this hook's state when the route changes sessionId in place
  // (/sessions/a/analytics -> /sessions/b/analytics renders the same element),
  // so the settings have to be re-read for the new key. Adjusting during
  // render rather than in an effect matters: the save effect below would
  // otherwise fire once with the new key and the previous session's settings,
  // overwriting them.
  const [loadedStorageKey, setLoadedStorageKey] = useState(storageKey);
  if (loadedStorageKey !== storageKey) {
    setLoadedStorageKey(storageKey);
    setSettings(readStoredSettings(storageKey));
  }

  // Save to localStorage whenever settings change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(settings));
    } catch (error) {
      console.warn("Failed to save analytics settings to localStorage:", error);
    }
  }, [storageKey, settings]);

  // Mirror of the latest settings for flush paths that cannot wait for (or
  // cannot trigger) a render: consecutive patches must compose even while the
  // state update from the previous patch is still in flight, and the storage
  // write below must see the pre-patch value.
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // Apply a patch immediately: updates state AND writes storage synchronously.
  // Used for flushes during unmount or pagehide, where a plain updateSetting
  // would update state but the save effect above would never run again.
  const persistPatch = useCallback(
    (patch: Partial<AnalyticsSettings>) => {
      const current = settingsRef.current;
      const changed = (Object.keys(patch) as (keyof AnalyticsSettings)[]).some(
        (key) => current[key] !== patch[key],
      );
      if (!changed) {
        return;
      }
      const next = { ...current, ...patch };
      settingsRef.current = next;
      setSettings(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch (error) {
        console.warn("Failed to save analytics settings to localStorage:", error);
      }
    },
    [storageKey],
  );

  // Helper to update specific setting
  const updateSetting = useCallback(
    <K extends keyof AnalyticsSettings>(
      key: K,
      value: AnalyticsSettings[K],
    ) => {
      setSettings((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  // Helper to update multiple settings at once
  const updateSettings = useCallback((updates: Partial<AnalyticsSettings>) => {
    setSettings((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  // Reset to defaults
  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  // Clear settings for this session
  const clearSettings = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setSettings(DEFAULT_SETTINGS);
    } catch (error) {
      console.warn("Failed to clear analytics settings:", error);
    }
  }, [storageKey]);

  // Export settings (for backup/sharing)
  const exportSettings = useCallback(() => {
    return JSON.stringify(settings, null, 2);
  }, [settings]);

  // Import settings (from backup/sharing)
  const importSettings = useCallback((settingsJson: string) => {
    try {
      const imported = JSON.parse(settingsJson);
      const merged = { ...DEFAULT_SETTINGS, ...imported };
      setSettings(merged);
      return true;
    } catch (error) {
      console.error("Failed to import settings:", error);
      return false;
    }
  }, []);

  return {
    settings,
    updateSetting,
    updateSettings,
    persistPatch,
    resetSettings,
    clearSettings,
    exportSettings,
    importSettings,
  };
}
